import { API_BASE_URL } from '../../config/api';
import { parseResponseJson, extractApiErrorMessage, performWebLogout, readStoredAuth } from './profileFormUtils';

export const CUSTOMER_ROLE_ID = 3;

/** Same rules as signup / createProfile password expectations. */
export function validateNewPasswordForApi(pw) {
  const s = String(pw || '');
  if (s.length < 8) return 'Password must be at least 8 characters.';
  if (/\s/.test(s)) return 'Password must not contain spaces.';
  if (!/[a-zA-Z]/.test(s)) return 'Password must include letters.';
  if (!/[0-9]/.test(s)) return 'Password must include numbers.';
  if (!/[^a-zA-Z0-9]/.test(s)) return 'Password must include a special character.';
  return '';
}

/** @param {string} phoneDigits - Same format as login/register, e.g. 919876543210 (no +) */
export async function sendForgotPasswordOtpPhone(phoneDigits) {
  const id = encodeURIComponent(phoneDigits);
  const res = await fetch(`${API_BASE_URL}/auth/sendOTP/forgotPassword/${id}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  const data = await parseResponseJson(res);
  return { res, data };
}

export async function sendForgotPasswordOtpEmail(email) {
  const id = encodeURIComponent(String(email).trim());
  const res = await fetch(`${API_BASE_URL}/auth/sendOTP/forgotPasswordEmail/${id}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  const data = await parseResponseJson(res);
  return { res, data };
}

export function forgotPasswordSendErrorMessage(status, data, channel) {
  const msg = extractApiErrorMessage(data);
  if (msg) return msg;
  if (status === 404) return 'Account not found. Try another phone or email, or sign up.';
  if (status === 400)
    return channel === 'email' ? 'Invalid email format.' : 'Invalid phone format.';
  if (status >= 500) return 'Could not send OTP. Try again later.';
  return 'Could not send OTP.';
}

export async function postForgotPasswordReset({ validationID, otp, password }) {
  const res = await fetch(`${API_BASE_URL}/auth/forgotPassword`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      validationID,
      otp: String(otp || '')
        .replace(/\D/g, '')
        .slice(0, 6),
      roleID: CUSTOMER_ROLE_ID,
      password,
    }),
  });
  const data = await parseResponseJson(res);
  return { res, data };
}

export function forgotPasswordResetErrorMessage(status, data) {
  const msg = extractApiErrorMessage(data);
  if (msg) return msg;
  if (status === 400) return 'Invalid OTP or password. Check and try again.';
  if (status === 404) return 'Account not found. Start the forgot password flow again.';
  if (status >= 500) return 'Something went wrong. Try again later.';
  return 'Could not reset password.';
}

/**
 * Change the password for the currently signed-in user.
 * @param {string} token Bearer token
 * @param {{ oldPassword: string, newPassword: string }} body
 */
export async function apiChangePassword(token, { oldPassword, newPassword }) {
  const res = await fetch(`${API_BASE_URL}/auth/changePassword`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ oldPassword, newPassword }),
  });
  const data = await parseResponseJson(res);
  return { res, data };
}

export function changePasswordErrorMessage(status, data) {
  const msg = extractApiErrorMessage(data);
  if (msg) return msg;
  if (status === 400) return 'Current password is incorrect, or the new password is invalid.';
  if (status === 401) return 'Please sign in again.';
  if (status === 404) return 'Account not found. Please sign in again.';
  if (status === 422) return 'Invalid password. Check the requirements and try again.';
  if (status >= 500) return 'Server error. Try again later.';
  return 'Could not change password.';
}

export async function apiDeleteAccount(token) {
  const res = await fetch(`${API_BASE_URL}/auth/deleteAccount`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await parseResponseJson(res);
  return { res, data };
}

/** Invalidate server session (Redis status). Failures are non-fatal for client sign-out. */
export async function apiLogout(token, { signal } = {}) {
  const res = await fetch(`${API_BASE_URL}/auth/logout`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    signal,
  });
  const data = await parseResponseJson(res);
  return { res, data };
}

/**
 * Exchange a refresh token for a new access token.
 * @param {string} refreshToken
 */
export async function apiRefreshAccessToken(refreshToken) {
  const res = await fetch(`${API_BASE_URL}/auth/refreshToken`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ refreshToken }),
  });
  const data = await parseResponseJson(res);
  return { res, data };
}

/**
 * Clear client session first, then best-effort API logout (Redis only).
 * Local storage must not wait on the network — API may be down in local dev.
 */
export async function performWebLogoutWithApi() {
  const { token } = readStoredAuth();
  performWebLogout();
  if (!token) return;
  try {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 5000);
    await apiLogout(token, { signal: controller.signal });
    window.clearTimeout(timeoutId);
  } catch {
    /* non-fatal */
  }
}

export function deleteAccountErrorMessage(status, data) {
  const msg = extractApiErrorMessage(data);
  if (msg) return msg;
  if (status === 401) return 'Please sign in again.';
  if (status === 404) return 'Account not found. Sign in again.';
  if (status >= 500) return 'Server error. Try again later.';
  return 'Could not delete account.';
}
